import { Note, Project, Tag } from '../../../_service/data.service';
import { FilterParams } from '../../../_service/filter.service';
import { TAG_FILTER_SCOPE, TagFilterScope, normalizeTagFilterScope } from '../../../_util/tag-scope.util';
import { ACTIVE_FILTER_STATUSES, effectiveStatus } from '../../../_util/note-status.util';
import { InlineTask, noteInlineTasks } from '../../../_util/inline-task.util';

/** Search types that look at inline tasks (Home "Tasks" view): a note matches when one of its tasks
 * does (or, for the second, its title or tags); the Tasks view then shows only the matching tasks. */
export const TASK_SEARCH_TYPES = ['Task title', 'Task, Note Title, Note Tags'] as const;

/** tasks/frontend-tagging-phase3.md — the current user's own private tag ids per note id, from the
 * private-tag-assignment API. `null` when unavailable (then private tags simply can't match). Keys
 * for notes not in the note list are ignored: private metadata only narrows notes the user already
 * has, it never adds one. */
export type PrivateTagIndex = ReadonlyMap<string, readonly string[]> | null;
import Delta from 'quill-delta';

/**
 * Pure filter logic extracted from HomeComponent.
 * Contains stateless functions for filtering, searching, and note categorization.
 */
export class FilterLogic {
  private static normalizeSearchableText(value: string | null | undefined): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  private static stripWhitespace(value: string): string {
    return value.replace(/\s+/g, '');
  }

  private static htmlToPlainText(html: string | null | undefined): string {
    if (!html) {
      return '';
    }

    // Lightweight conversion suitable for search indexing in both browser and test environments.
    return html
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private static isValidDate(value: unknown): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
  }

  private static noteMatchesFullText(note: Note, searchText: string): boolean {
    const normalizedSearch = FilterLogic.normalizeSearchableText(searchText);
    if (!normalizedSearch) {
      return true;
    }

    const rawDescriptionHtml = FilterLogic.normalizeSearchableText(note.DescriptionHtml);
    const rawCommentsHtml = FilterLogic.normalizeSearchableText(note.CommentsHtml);
    const plainDescription = FilterLogic.normalizeSearchableText(
      FilterLogic.htmlToPlainText(note.DescriptionHtml),
    );
    const plainComments = FilterLogic.normalizeSearchableText(
      FilterLogic.htmlToPlainText(note.CommentsHtml),
    );

    // Also support words broken by inline HTML tags by comparing whitespace-stripped forms.
    const compactSearch = FilterLogic.stripWhitespace(normalizedSearch);
    const searchableValues = [
      FilterLogic.normalizeSearchableText(note.Title),
      FilterLogic.normalizeSearchableText(note.Keywords),
      FilterLogic.normalizeSearchableText(note.AiKeywords),
      rawDescriptionHtml,
      rawCommentsHtml,
      plainDescription,
      plainComments,
    ];

    return searchableValues.some((value) => {
      if (!value) {
        return false;
      }
      return value.includes(normalizedSearch) || FilterLogic.stripWhitespace(value).includes(compactSearch);
    });
  }

  /** Text search over tag names: the note's Space tags and (when known) the user's private tags on it. */
  private static noteMatchesTagSearch(
    note: Note,
    searchText: string,
    tagById: Map<string, Tag>,
    privateTagIndex: PrivateTagIndex = null,
  ): boolean {
    const tags = [...(Array.isArray(note.Tags) ? note.Tags : []), ...(privateTagIndex?.get(note._id) ?? [])];
    if (!tags.length) {
      return false;
    }

    return tags.some((tag) => {
      if (typeof tag === 'string') {
        const resolvedTag = tagById.get(tag);
        if (resolvedTag) {
          const resolvedValues = [resolvedTag.name, resolvedTag.slug, resolvedTag.tagType, resolvedTag._id];
          return resolvedValues.some((value) => String(value ?? '').toLowerCase().includes(searchText));
        }
        return tag.toLowerCase().includes(searchText);
      }

      const values = [tag.name, tag.slug, tag.tagType, tag._id];
      return values.some((value) => String(value ?? '').toLowerCase().includes(searchText));
    });
  }

  private static normalizeTagName(name: string): string {
    return name.trim().toLowerCase();
  }

  /** What a selected filter tag matches: its NAME (so identically named Space/private tags, and Space
   * tags of different Spaces, all match), or — for an id no longer in the catalog, e.g. a tag deleted
   * since a search was saved — exactly that id. */
  static tagCriterionKey(tagId: string, tagById: ReadonlyMap<string, Tag>): string {
    const name = tagById.get(tagId)?.name;
    return name && name.trim() ? `n:${FilterLogic.normalizeTagName(name)}` : `i:${tagId}`;
  }

  /** The keys (see tagCriterionKey) of the tags a note carries within `scope`: its Space tags
   * (Note.Tags) for SPACE/ANY, the current user's private tags on it for PRIVATE/ANY. */
  static noteTagKeys(
    note: Note,
    scope: TagFilterScope,
    tagById: ReadonlyMap<string, Tag>,
    privateTagIndex: PrivateTagIndex,
  ): Set<string> {
    const ids: string[] = [];
    if (scope !== TAG_FILTER_SCOPE.PRIVATE) {
      for (const tag of Array.isArray(note.Tags) ? note.Tags : []) {
        ids.push(typeof tag === 'string' ? tag : String(tag?._id ?? ''));
      }
    }
    if (scope !== TAG_FILTER_SCOPE.SPACE) {
      ids.push(...(privateTagIndex?.get(note._id) ?? []));
    }

    const keys = new Set<string>();
    for (const id of ids) {
      if (!id) continue;
      keys.add(`i:${id}`);
      keys.add(FilterLogic.tagCriterionKey(id, tagById));
    }
    return keys;
  }

  /**
   * Convert Quill Delta to plain text
   */
  static deltaToPlainText(delta: Delta | null | undefined): string {
    if (!delta) return '';
    return delta.ops.map((op: any) => (typeof op.insert === 'string' ? op.insert : '')).join('');
  }

  /**
   * Generate a human-readable time prefix (e.g., "3m: " for 3 months ago)
   */
//   static getNoteYearPrefix(note: Note): string {
//     const now = new Date();
//     const modified = new Date(note.last_modified);

//     const yearsDiff = now.getFullYear() - modified.getFullYear();
//     const monthsDiff = now.getMonth() - modified.getMonth();
//     let totalMonths = yearsDiff * 12 + monthsDiff;

//     // If current day is less than the modified day, subtract 1 (not full month yet)
//     if (now.getDate() < modified.getDate()) {
//       totalMonths = totalMonths - 1;
//     }

//     if (totalMonths) {
//       return `${totalMonths}m: `;
//     } else {
//       return '';
//     }
//   }

  /**
   * Get all notes for a project, sorted by modification date (newest first)
   */
  static getLastModifiedNotes(projectId: number, filteredNotes: Note[]): Note[] {
    const notes = filteredNotes.filter((note) => note.Project === projectId);
    return notes.sort((a, b) => {
      const dateA = new Date(a.last_modified);
      const dateB = new Date(b.last_modified);
      return dateB.getTime() - dateA.getTime(); // Descending order
    });
  }

  /**
   * Count how many filtered notes belong to a project
   */
  static calculateItemCount(projectId: number, filteredNotes: Note[]): number {
    return filteredNotes.filter((note) => note.Project === projectId).length;
  }

  /**
   * Apply all active filters to the note list
   */
  /** Whether a task (of a note that passed applyFilters) is shown in the Tasks view: with a task search
   * type, only tasks matching the search; with any other search type the note-level search already
   * decided, so all of the note's tasks are shown. */
  static taskMatchesSearch(
    task: Pick<InlineTask, 'description'>,
    note: Note,
    filter: FilterParams,
    availableTags: Tag[] = [],
    privateTagIndex: PrivateTagIndex = null,
  ): boolean {
    const searchText = String(filter.SearchString ?? '').toLowerCase().trim();
    if (!searchText) return true;
    const searchType = String(filter.SearchType ?? '').trim().toLowerCase();
    const taskHit = (task.description ?? '').toLowerCase().includes(searchText);
    if (searchType === 'task title') return taskHit;
    if (searchType === 'task, note title, note tags') {
      if (taskHit || (note.Title ?? '').toLowerCase().includes(searchText)) return true;
      const tagById = new Map<string, Tag>(availableTags.map((tag) => [tag._id, tag]));
      return FilterLogic.noteMatchesTagSearch(note, searchText, tagById, privateTagIndex);
    }
    return true;
  }

  static applyFilters(
    allNotes: Note[],
    filter: FilterParams,
    projects: Project[],
    availableTags: Tag[] = [],
    favoriteNoteIds: ReadonlySet<string> = new Set(),
    privateTagIndex: PrivateTagIndex = null,
  ): Note[] {
    // console.log('Applying filters:', filter);
    let filteredData = allNotes;
    const tagById = new Map<string, Tag>(availableTags.map((tag) => [tag._id, tag]));

    // Tag filters (tasks/frontend-tagging-phase3.md). A selected tag stands for its name within
    // filter.TagScope, so "Architecture" + ANY matches a Space tag or a private tag named
    // Architecture, in any Space. See tagCriterionKey()/noteTagKeys().
    const tagScope = normalizeTagFilterScope(filter.TagScope);
    const keysByNote = new Map<string, Set<string>>();
    const noteKeys = (note: Note) => {
      let keys = keysByNote.get(note._id);
      if (!keys) {
        keys = FilterLogic.noteTagKeys(note, tagScope, tagById, privateTagIndex);
        keysByNote.set(note._id, keys);
      }
      return keys;
    };

    if (Array.isArray(filter.TagIds) && filter.TagIds.length > 0) {
      const requiredKeys = [...new Set(filter.TagIds.filter((tagId) => !!tagId).map((id) => FilterLogic.tagCriterionKey(id, tagById)))];
      const includeMode = filter.TagIncludeMode === 'OR' ? 'OR' : 'AND';
      filteredData = filteredData.filter((note) => {
        const keys = noteKeys(note);
        if (!keys.size) {
          return false;
        }
        if (includeMode === 'OR') {
          return requiredKeys.some((key) => keys.has(key));
        }
        return requiredKeys.every((key) => keys.has(key));
      });
    }

    // Exclude-tag filter: notes containing ANY of the excluded tags are removed.
    // This uses OR logic — a note is hidden if it has even one excluded tag.
    if (Array.isArray(filter.ExcludeTagIds) && filter.ExcludeTagIds.length > 0) {
      const excludedKeys = filter.ExcludeTagIds.filter((id) => !!id).map((id) => FilterLogic.tagCriterionKey(id, tagById));
      filteredData = filteredData.filter((note) => {
        const keys = noteKeys(note);
        return !excludedKeys.some((key) => keys.has(key));
      });
    }

    // Search filter
    if (filter.SearchString) {
      const rawSearchType = String(filter.SearchType ?? '').trim();
      const searchType = rawSearchType.toLowerCase();
      const searchText = filter.SearchString.toLowerCase().trim();

      const matchesTitle = (item: Note) => item.Title?.toLowerCase().includes(searchText);
      const matchesKeywords = (item: Note) =>
        (item.Keywords || '').toLowerCase().includes(searchText) ||
        (item.AiKeywords || '').toLowerCase().includes(searchText);
      const matchesTags = (item: Note) => FilterLogic.noteMatchesTagSearch(item, searchText, tagById, privateTagIndex);

      if (searchType === 'semantic') {
        // Semantic mode is handled in HomeComponent by calling the semantic search API.
      } else if (searchType === 'full text') {
        filteredData = filteredData.filter((item) => FilterLogic.noteMatchesFullText(item, searchText));
      } else if (searchType === 'title only') {
        filteredData = filteredData.filter((item) => matchesTitle(item));
      } else if (searchType === 'title & tags') {
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesTags(item));
      } else if (searchType === 'tags only') {
        filteredData = filteredData.filter((item) => matchesTags(item));
      } else if (
        searchType === 'title + keywords + tags' ||
        searchType === 'title, keywords & tags' ||
        searchType === 'title & keywords & tags'
      ) {
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesKeywords(item) || matchesTags(item));
      } else if (!searchType || searchType === 'title & keywords') {
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesKeywords(item));
      } else if (searchType === 'keywords only') {
        filteredData = filteredData.filter((item) => matchesKeywords(item));
      } else if (searchType === 'task title' || searchType === 'task, note title, note tags') {
        filteredData = filteredData.filter((item) =>
          noteInlineTasks(item).some((task) => FilterLogic.taskMatchesSearch(task, item, filter, availableTags, privateTagIndex)),
        );
      } else if (searchType === 'id') {
        filteredData = filteredData.filter((item) => (item._id || '').toLowerCase().includes(searchText));
      } else {
        // Unknown search type should never disable search completely.
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesKeywords(item) || matchesTags(item));
      }
    }

    // Type filter
    if (filter.Type) {
      filteredData = filteredData.filter((item) => item.NoteType && item.NoteType.includes(filter.Type!));
    }

    // Project filter
    if (filter.ProjectName) {
      const filterProject = filter.ProjectName.toLowerCase().trim();

      if (filterProject === '(not work)') {
        filteredData = filteredData.filter((item) => {
          const project = projects?.find((p) => p.ID === item.Project);
          const projectName = project?.Name?.toLowerCase().trim() || '';
          return !projectName.includes('work');
        });
      } else {
        filteredData = filteredData.filter((item) => {
          const project = projects?.find((p) => p.ID === item.Project);
          const projectName = project?.Name?.toLowerCase().trim() || '';
          return projectName.includes(filterProject);
        });
      }
    }

    // Status filter
    if (filter.Status && filter.Status !== '(All)') {
      if (filter.Status === '(All active)') {
        filteredData = filteredData.filter((item) => ACTIVE_FILTER_STATUSES.some((status) => item.Status?.includes(status)));
      } else {
        // A status also matches notes still carrying the legacy status it replaced (Inactive ⇐ Completed/
        // Expired, …), and a saved filter on a legacy status matches the notes already migrated from it.
        const wanted = effectiveStatus(filter.Status);
        filteredData = filteredData.filter(
          (item) => !!item.Status && (item.Status.includes(filter.Status!) || effectiveStatus(item.Status) === wanted),
        );
      }
    }

    // Favorites filter
    if (filter.FavoritesOnly) {
      filteredData = filteredData.filter((item) => favoriteNoteIds.has(item._id));
    }

    // Space filter (tasks/space_ux.md) — empty/omitted SpaceIds means "All" (no filtering).
    if (Array.isArray(filter.SpaceIds) && filter.SpaceIds.length > 0) {
      const selectedSpaceIds = new Set(filter.SpaceIds);
      filteredData = filteredData.filter((item) => item.spaceId && selectedSpaceIds.has(item.spaceId));
    }

    // Modified date range filter
    const modifiedStart = filter.ModifiedBetween?.start;
    const modifiedEnd = filter.ModifiedBetween?.end;
    const hasModifiedBounds = FilterLogic.isValidDate(modifiedStart) || FilterLogic.isValidDate(modifiedEnd);
    if (hasModifiedBounds) {
      const start = FilterLogic.isValidDate(modifiedStart) ? modifiedStart : null;
      const end = FilterLogic.isValidDate(modifiedEnd) ? modifiedEnd : null;

      filteredData = filteredData.filter((item) => {
        // parse item's date safely
        const itemDate = item.last_modified ? new Date(item.last_modified) : null;
        if (!itemDate || isNaN(itemDate.getTime())) return false; // skip invalid

        let ok = true;

        if (start) {
          // exclude notes modified before start
          ok = ok && itemDate >= start;
        }
        if (end) {
          // exclude notes modified after end
          ok = ok && itemDate <= end;
        }

        return ok;
      });
    }

    // Created date range filter
    const createdStart = filter.CreatedBetween?.start;
    const createdEnd = filter.CreatedBetween?.end;
    const hasCreatedBounds = FilterLogic.isValidDate(createdStart) || FilterLogic.isValidDate(createdEnd);
    if (hasCreatedBounds) {
      const start = FilterLogic.isValidDate(createdStart) ? createdStart : null;
      const end = FilterLogic.isValidDate(createdEnd) ? createdEnd : null;

      filteredData = filteredData.filter((item) => {
        const itemDate = item.created_on ? new Date(item.created_on) : null;
        if (!itemDate || isNaN(itemDate.getTime())) return false;

        let ok = true;

        if (start) {
          ok = ok && itemDate >= start;
        }
        if (end) {
          ok = ok && itemDate <= end;
        }

        return ok;
      });
    }

    return filteredData;
  }
}
